import {
  AnimatedContentLoader,
  Button,
  FilePreview,
  FileSelector,
  ModalV2,
  TextTooltip,
  uploadFileWithToast
} from '@components'
import { getFilesPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useTheme } from '@hooks'
import { fetchFile, uploadFile } from '@query'
import { getAttachmentNoteTooltipContent, hasAttachmentNote } from '@utils/attachmentNotes'
import { motion } from 'framer-motion'
import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'
import { FaArrowRight, FaStickyNote, FaTimes } from 'react-icons/fa'
import { FaMagnifyingGlassPlus } from 'react-icons/fa6'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import {
  StyledAttachmentSelector,
  StyledEmptyAttachmentSelector
} from './AttachmentSelector.styled'
import { AttachmentVisualTone, getAttachmentVisual } from './attachmentVisual'

interface Props {
  value: number[] // list of selected attachment ids
  onChange?: (value: number[]) => void
  showRemoveInsteadOfViewLink?: boolean
  disabled?: boolean
  appearance?: 'default' | 'dropzone' | 'section'
  hideEmptyDescription?: boolean
  compactSectionEmpty?: boolean
}

interface AttachmentDropHintProps {
  onOpen: () => void
  onShowArchive?: () => void
}

const AttachmentDropHint: React.FC<AttachmentDropHintProps> = ({ onOpen, onShowArchive }) => {
  if (onShowArchive) {
    return (
      <Trans i18nKey="attachmentSelector.dragOrAdd">
        <a key="link-1" href="#" onClick={onOpen} />
        <a key="link-2" href="#" onClick={onShowArchive} />
      </Trans>
    )
  }

  return (
    <Trans i18nKey="attachmentSelector.dragOrAddFromComputer">
      <a key="link-1" href="#" onClick={onOpen} />
    </Trans>
  )
}

export const AttachmentSelector: React.FC<Props> = ({
  value: attachmentIds = [],
  onChange,
  showRemoveInsteadOfViewLink,
  disabled,
  appearance = 'default',
  hideEmptyDescription = false,
  compactSectionEmpty = false
}) => {
  if (!disabled && !onChange) {
    throw new Error('onChange is required when AttachmentSelector is enabled')
  }

  const { t } = useTranslation()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const canEditFiles = usePermission(PermissionSet.FILES_EDIT)
  const [showArchive, setShowArchive] = useState(false)
  const effectiveAppearance = attachmentIds.length === 0 ? appearance : 'default'

  const { slug } = useBusinessContext()
  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: disabled,
    noDragEventsBubbling: true,
    onDrop: async newFiles => {
      const promises = newFiles.map(file =>
        uploadFileWithToast(
          file,
          options => uploadFile(file, { slug, fileName: file.name }, options),
          {
            slug,
            canEditNotes: canEditFiles,
            queryClient,
            theme
          }
        )
      )
      const results = await Promise.all(promises)
      const uploadedIds = results.flatMap(file => (file ? [file.id] : []))
      if (uploadedIds.length > 0) onChange([...attachmentIds, ...uploadedIds])
    }
  })

  return (
    <>
      {!disabled && attachmentIds.length === 0 && (
        <StyledEmptyAttachmentSelector
          $isDragActive={isDragActive}
          $appearance={effectiveAppearance}
          $compactSectionEmpty={compactSectionEmpty}
          {...getRootProps()}
          data-test="file-upload-dropzone"
        >
          <motion.div className="add-more">
            <AttachmentDropHint
              onOpen={() => open()}
              onShowArchive={canEditFiles ? () => setShowArchive(true) : undefined}
            />
          </motion.div>
          <input {...getInputProps()} />
        </StyledEmptyAttachmentSelector>
      )}

      {attachmentIds.length > 0 && (
        <StyledAttachmentSelector
          $isDragActive={isDragActive}
          $appearance={effectiveAppearance}
          $compact={Boolean(disabled)}
          {...getRootProps()}
        >
          <AttachmentOverview
            attachmentIds={attachmentIds}
            onChange={onChange}
            showRemoveInsteadOfViewLink={showRemoveInsteadOfViewLink}
            disabled={disabled}
            hideEmptyDescription={hideEmptyDescription}
          />

          {!disabled && (
            <>
              <div className="add-more">
                <AttachmentDropHint
                  onOpen={() => open()}
                  onShowArchive={canEditFiles ? () => setShowArchive(true) : undefined}
                />
              </div>
              <input {...getInputProps()} />
            </>
          )}
        </StyledAttachmentSelector>
      )}

      {canEditFiles && (
        <ModalV2
          header={t('attachmentSelector.title')}
          width={700}
          height={900}
          isVisible={showArchive}
          handleOnClose={() => setShowArchive(false)}
        >
          <FileSelector
            slug={slug}
            initialSelections={attachmentIds}
            onClose={ids => {
              onChange(ids)
              setShowArchive(false)
            }}
          />
        </ModalV2>
      )}
    </>
  )
}

interface AttachmentOverviewProps {
  attachmentIds: number[]
  onChange: (ids: number[]) => void
  showRemoveInsteadOfViewLink?: boolean
  disabled?: boolean
  hideEmptyDescription?: boolean
}

export const AttachmentOverview: React.FC<AttachmentOverviewProps> = ({
  attachmentIds,
  onChange,
  showRemoveInsteadOfViewLink = false,
  disabled = false,
  hideEmptyDescription = false
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const history = useHistory()

  const results = useQueries({
    queries: attachmentIds.map(fileId => ({
      queryKey: [slug, 'files', fileId],
      queryFn: () => fetchFile({ slug, fileId }),
      enabled: Boolean(fileId)
    }))
  })
  const isLoading = results.some(result => result.isLoading)
  // Show whatever the BE allows; do not fail the whole list if one id 404s.
  const files = results.filter(result => result.isSuccess && result.data).map(result => result.data)

  return (
    <AnimatedContentLoader
      isLoading={isLoading}
      isEmpty={files.length === 0}
      isEmptyDescription={hideEmptyDescription ? undefined : t('document.noAttachments')}
    >
      <FileWrapper>
        {files.map(file => {
          const { Icon, tone } = getAttachmentVisual(file.type, file.name)

          return (
            <FilePreview key={file.id} fileId={file.id}>
              <AttachmentRow>
                <IconBadge $tone={tone}>
                  <Icon />
                </IconBadge>

                <FileName title={file.name}>{file.name}</FileName>

                <AttachmentRowActions>
                  {hasAttachmentNote(file.notes) && (
                    <TextTooltip
                      tooltip={getAttachmentNoteTooltipContent(file.notes, t('files.notes.empty'))}
                      maxWidth={360}
                      placement="left"
                    >
                      <Button
                        type="button"
                        isSecondary
                        size="sm"
                        className="attachment-note-button"
                        icon={<FaStickyNote />}
                        aria-label={t('files.notes.label')}
                        onClick={event => event.stopPropagation()}
                        onMouseDown={event => event.stopPropagation()}
                        onPointerDown={event => event.stopPropagation()}
                      />
                    </TextTooltip>
                  )}

                  <Button
                    type="button"
                    isSecondary
                    size="sm"
                    icon={<FaMagnifyingGlassPlus />}
                    aria-label={t('attachmentSelector.preview')}
                  />

                  {showRemoveInsteadOfViewLink ? (
                    !disabled && (
                      <Button
                        type="button"
                        isSecondary
                        size="sm"
                        icon={<FaTimes />}
                        aria-label={t('documentRelations.remove')}
                        onClick={e => {
                          e.stopPropagation()
                          onChange(attachmentIds.filter(id => id !== file.id))
                        }}
                      />
                    )
                  ) : (
                    <Button
                      type="button"
                      isSecondary
                      size="sm"
                      icon={<FaArrowRight />}
                      aria-label={t('attachmentSelector.openInFiles')}
                      onClick={e => {
                        e.stopPropagation()
                        history.push(
                          getFilesPageUrl(slug, {
                            id: file.id,
                            idType: 'file',
                            folder: file.folder_id
                          })
                        )
                      }}
                    />
                  )}
                </AttachmentRowActions>
              </AttachmentRow>
            </FilePreview>
          )
        })}
      </FileWrapper>
    </AnimatedContentLoader>
  )
}

const FileWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  overflow: hidden;

  & > * {
    width: 100%;
  }

  & > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  }
`

const AttachmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const AttachmentRowActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 1px;

  button {
    color: ${({ theme }) => theme.colors.iconGray};

    &&:hover,
    &&:focus,
    &&:active {
      color: ${({ theme }) => theme.colors.metalGray};
      background: ${({ theme }) => theme.colors.defaultHover};
    }
  }

  button.attachment-note-button {
    &&:hover,
    &&:focus,
    &&:active {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.defaultHover};
    }
  }
`

const IconBadge = styled.div<{ $tone: AttachmentVisualTone }>`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }

  ${({ theme, $tone }) => {
    switch ($tone) {
      case 'pdf':
        return `
          background: ${theme.colors.error}1a;
          color: ${theme.colors.error};
        `
      case 'image':
      case 'spreadsheet':
        return `
          background: ${theme.colors.success}1a;
          color: ${theme.colors.success};
        `
      case 'document':
        return `
          background: ${theme.colors.primary}1a;
          color: ${theme.colors.primary};
        `
      default:
        return `
          background: ${theme.colors.neutralGray};
          color: ${theme.colors.iconGray};
        `
    }
  }}
`

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.menuBg};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
